import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import ProductDetailModal from "./ProductDetailModal";
import LoadingAM from "../assets/rigve_loader.gif";

export const ProductGridmainSection = () => {
  const { id, main_cat_id } = useParams();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);

  // Use id directly from params or fallback to state
  const selectedCategory = id;

  const [seoData, setSeoData] = useState({
    title: "Professional Products Pattern",
    description:
      "Discover our comprehensive collection of professional products with advanced patterns and designs.",
    keywords: "professional products, patterns, designs, quality products",
    canonicalUrl: window.location.href,
  });

  // Fetch Categories whenever main_cat_id changes
  const fetchCategories = useCallback(async () => {
    if (!main_cat_id) return;
    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/get_category?main_cat_id=${main_cat_id}`,
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setCategories(data?.data || []);
    } catch (err) {
      setError(err.message);
    }
  }, [main_cat_id]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Find selected category data dynamically using useMemo so it updates instantly when `id` or `categories` change
  const selectedCategoryData = useMemo(() => {
    if (!selectedCategory || categories.length === 0) return null;
    return categories.find((cat) => cat.id === parseInt(selectedCategory));
  }, [selectedCategory, categories]);

  // Fetch Products whenever selectedCategory or main_cat_id changes
  const fetchProducts = useCallback(async () => {
    if (!main_cat_id || !selectedCategory) return;
    try {
      setIsLoading(true);
      setError(null);

      const firstRes = await fetch(
        `https://webadmin.rigve.com/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${selectedCategory}&page=1`,
      );
      if (!firstRes.ok) throw new Error("Failed to fetch products");
      const firstData = await firstRes.json();

      let allProducts = firstData.data?.data || [];
      const lastPage = firstData.data?.last_page || 1;
      const total = firstData.data?.total || allProducts.length;

      if (lastPage > 1) {
        const pagePromises = [];
        for (let p = 2; p <= lastPage; p++) {
          pagePromises.push(
            fetch(
              `https://webadmin.rigve.com/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${selectedCategory}&page=${p}`,
            ).then((res) => res.json()),
          );
        }
        const results = await Promise.all(pagePromises);
        results.forEach((res) => {
          if (res.data?.data) {
            allProducts = [...allProducts, ...res.data.data];
          }
        });
      }

      setProducts(allProducts);
      setTotalProducts(total || allProducts.length);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, main_cat_id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const memoizedProducts = useMemo(() => products, [products]);

  // Update SEO and Meta Tags whenever category data or products change
  useEffect(() => {
    if (selectedCategoryData) {
      setSeoData({
        title: `${selectedCategoryData.name} - Professional Products Pattern`,
        description:
          selectedCategoryData.description ||
          `Browse our ${selectedCategoryData.name} collection with ${totalProducts} professional products.`,
        keywords: `${selectedCategoryData.name}, professional products, patterns, ${selectedCategoryData.name} designs`,
        canonicalUrl: window.location.href,
      });
    } else if (categories.length > 0) {
      setSeoData({
        title: "Professional Products Pattern - All Categories",
        description: `Explore our complete collection of ${totalProducts} professional products across all categories.`,
        keywords:
          "professional products, all categories, patterns, designs, quality products",
        canonicalUrl: window.location.href,
      });
    }
  }, [selectedCategoryData, categories, totalProducts]);

  useEffect(() => {
    document.title = seoData.title;

    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const metaCanonical = document.querySelector('link[rel="canonical"]');

    if (metaDescription) {
      metaDescription.setAttribute("content", seoData.description);
    }

    if (metaKeywords) {
      metaKeywords.setAttribute("content", seoData.keywords);
    }

    if (metaCanonical) {
      metaCanonical.setAttribute("href", seoData.canonicalUrl);
    }

    // Generate structured data for SEO
    const generateStructuredData = () => {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoData.title,
        description: seoData.description,
        url: seoData.canonicalUrl,
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: totalProducts,
          itemListElement: memoizedProducts.map((product, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Product",
              name: product.title,
              description: product.description || product.title,
              image: product.image,
              category: product.category,
              url: `${window.location.origin}/product/${product.id
                }/${currentPage}/${main_cat_id}/${selectedCategory || id}/${product.title
                }`,
            },
          })),
        },
      };

      if (selectedCategoryData) {
        structuredData.mainEntity.about = {
          "@type": "Thing",
          name: selectedCategoryData.name,
          description: selectedCategoryData.description,
        };
      }

      return JSON.stringify(structuredData);
    };

    // Insert structured data into the head
    let script = document.getElementById("structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.text = generateStructuredData();
  }, [
    seoData,
    memoizedProducts,
    totalProducts,
    selectedCategoryData,
    currentPage,
    main_cat_id,
    selectedCategory,
    id,
  ]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[80vh] w-full py-24">
        <div className="text-center">
          <img
            src={LoadingAM}
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4"
            alt="Loading products..."
            width="80"
            height="80"
          />
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Products
          </h2>
          <p className="text-red-500 text-sm sm:text-base">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );

  return (
    <main className="w-full overflow-x-hidden">
      {/* HERO BANNER - FULL WIDTH */}
      <section className="w-full">
        <div className="relative w-full overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-orange-500/30 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 flex flex-col lg:flex-row w-full min-h-[380px]">
            <div className="w-full h-[320px] sm:h-[380px] lg:h-auto lg:w-2/5 xl:w-1/3 flex-shrink-0">
              <img
                src={selectedCategoryData?.image}
                alt={selectedCategoryData?.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 text-white p-6 sm:p-8 lg:p-10 flex flex-col justify-center max-w-5xl">
              <p className="text-orange-400 uppercase text-xs sm:text-sm font-semibold mb-3">
                Explore Product
              </p>

              <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">
                {selectedCategoryData?.name || "Loading Category..."}
              </h2>

              <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-3xl">
                {selectedCategoryData?.description ||
                  "Please wait while we load category details."}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {memoizedProducts && memoizedProducts.length > 0 && (
          <section className="pb-16">
            <div className="my-5">
              <h3 className="text-lg sm:text-2xl text-center font-bold text-gray-900">
                Patterns & Collections
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {memoizedProducts.map((product) => (
                <div
                  key={product.id}
                  className="group relative bg-white border border-gray-200 rounded-xl flex flex-col transition-all duration-500 hover:shadow-md hover:border-transparent hover:-translate-y-2"
                >
                  <div className="relative overflow-hidden rounded-xl rounded-b-none bg-gray-100">
                    <a
                      href={`/product/${product.id}/1/${main_cat_id}/${
                        selectedCategory
                      }/${encodeURIComponent(product.title)}`}
                    >
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-40 md:h-48 object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </a>
                  </div>

                  <div className="flex-grow p-3">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 mb-1">
                      {product.title}
                    </h3>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        onClick={() =>
                          (window.location.href = `/product/${
                            product.id
                          }/1/${main_cat_id}/${
                            selectedCategory
                          }/${encodeURIComponent(product.title)}`)
                        }
                        className="col-span-1 text-[11px] font-bold py-2.5 rounded-lg bg-slate-200 transition-all"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="col-span-1 text-[11px] font-bold py-2.5 rounded-lg bg-orange-200 transition-all"
                      >
                        Inquiry
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </div>
    </main>
  );
};
